import React from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const AwarenessKnowledgeTab = () => {
  // askNivi Journey Color Palette
  const colors = {
    // Journey stages
    reached: '#F17DB1',      // Pink - askNivi branding
    onboarded: '#4FBFA5',    // Teal - Nivi branding  
    engaged: '#812990',      // Purple - engagement
    targetEngaged: '#F37021', // Orange - target users
    journeyStarted: '#FFC20E', // Yellow - momentum
    expressedIntent: '#5F9EA0', // Cadet blue - intent
    recommendation: '#9E7BFF', // Lavender - recommendations
    referral: '#BA55D3',     // Purple - key milestone
    readiness: '#6A5ACD',    // Slate blue - commitment
    redemption: '#005BE7',   // Blue - ultimate goal
    
    // Universal colors
    defaultBar: '#4361EE',   // Default bar chart
    woman: '#FF6B6B',        // Gender - woman
    man: '#4D96FF',          // Gender - man
    nonBinary: '#9D4EDD',    // Gender - non-binary
    
    // Text and backgrounds
    text: '#2D3142',
    lightGray: '#F8F9FA'
  };

  // Key findings - focusing on positive market opportunities
  const keyFindings = [
    {
      value: "52%",
      label: "Cervical Cancer Awareness",
      description: "Population with some level of cervical cancer awareness (Higher Awareness Group)",
      color: colors.onboarded
    },
    {
      value: "24.3%",
      label: "High Knowledge Among Aware",
      description: "Strong foundation for advanced health education programs",
      color: colors.engaged
    },
    {
      value: "49.4%",
      label: "Knowledge Gap Opportunity",
      description: "Population requiring targeted education interventions across both segments",
      color: colors.journeyStarted
    }
  ];

  // Demographic breakdown data
  const demographicInfo = [
    { label: "Total Respondents:", value: "2,135 participants (MRP-adjusted for population representativeness)" },
    { label: "Age Cohorts:", value: "15-24 years: 38% | 25-34 years: 40% | 35-45 years: 22%" },
    { label: "Education Breakdown:", value: "Secondary: 53% | Primary: 22% | University: 20% | Postgraduate: 5%" },
    { label: "Gender Distribution:", value: "Women: 51% | Men: 49% | Non-binary: <1%" },
    { label: "Geographic Focus:", value: "Lagos & Ogun State urban populations" }
  ];

  // Knowledge levels by awareness segment
  const knowledgeByAwareness = {
    someAwareness: {
      title: "HPV/Cervical Cancer Knowledge (Higher Awareness Group)",
      subtitle: "Combining High + Medium Awareness Populations",
      totalPopulation: "52% of population",
      insight: "Population with existing cervical cancer awareness - ready for targeted HPV education programs",
      data: [
        { level: "High Knowledge", percentage: 24.3, color: colors.redemption },
        { level: "Medium Knowledge", percentage: 43.9, color: colors.expressedIntent },
        { level: "Low Knowledge", percentage: 31.8, color: colors.reached }
      ]
    },
    lowNoAwareness: {
      title: "Reproductive Health Knowledge (Lower Awareness Group)", 
      subtitle: "Combining Low + No Awareness Populations",
      totalPopulation: "48% of population",
      insight: "Population with minimal cervical cancer awareness - foundation health education opportunity",
      data: [
        { level: "High Knowledge", percentage: 3.0, color: colors.redemption },
        { level: "Medium Knowledge", percentage: 31.6, color: colors.expressedIntent },
        { level: "Low Knowledge", percentage: 65.4, color: colors.reached }
      ]
    }
  };

  // Demographic comparisons - side-by-side donuts highlighting high knowledge
  const demographicComparisons = [
    {
      title: "Knowledge by Gender",
      subtitle: "HPV/Cervical Cancer High Knowledge Rates",
      data: [
        { 
          category: "Men", 
          highKnowledge: 54.3, 
          population: "49%",
          color: colors.man,
          textColor: "white"
        },
        { 
          category: "Women", 
          highKnowledge: 65.8, 
          population: "51%",
          color: colors.woman,
          textColor: "black"
        }
      ],
      insight: "Women show 11.5 percentage point advantage in HPV knowledge, highlighting opportunity for male-focused education programs"
    },
    {
      title: "Knowledge by Education",
      subtitle: "Reproductive Health High Knowledge Rates", 
      data: [
        {
          category: "Primary Education",
          highKnowledge: 47.9,
          population: "22%",
          color: colors.targetEngaged,
          textColor: "black"
        },
        {
          category: "Secondary Education", 
          highKnowledge: 51.7,
          population: "53%",
          color: colors.onboarded,
          textColor: "black"
        },
        {
          category: "University+",
          highKnowledge: 68.8,
          population: "25%",
          color: colors.engaged,
          textColor: "white"
        }
      ],
      insight: "Clear education gradient shows 20.9 percentage point improvement from primary to university, indicating scalable knowledge-building approach"
    },
    {
      title: "Knowledge by Age Group",
      subtitle: "Combined Knowledge Assessment Performance",
      data: [
        {
          category: "Ages 15-24",
          highKnowledge: 50.2,
          population: "38%",
          color: colors.journeyStarted,
          textColor: "black"
        },
        {
          category: "Ages 25-34",
          highKnowledge: 51.4,
          population: "40%", 
          color: colors.redemption,
          textColor: "white"
        },
        {
          category: "Ages 35-45",
          highKnowledge: 49.8,
          population: "22%",
          color: colors.readiness,
          textColor: "white"
        }
      ],
      insight: "Consistent knowledge levels across age groups (49-51%) indicate need for broad-based education programs rather than age-specific targeting"
    }
  ];

  // Awareness level distribution - positive framing
  const awarenessLevels = [
    { level: "High Awareness", percentage: 32.8, description: "\"Yes, know more\" - Ready for advanced programs", color: colors.engaged },
    { level: "Medium Awareness", percentage: 18.2, description: "\"Yes, know little\" - Engagement opportunity", color: colors.onboarded },
    { level: "Low Awareness", percentage: 16.4, description: "\"Heard the name\" - Basic education entry", color: colors.reached },
    { level: "No Awareness", percentage: 32.6, description: "\"Never heard\" - Primary education opportunity", color: colors.journeyStarted }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="text-sm font-medium">{`${label}: ${payload[0].value}%`}</p>
        </div>
      );
    }
    return null;
  };

  const renderKnowledgeByAwareness = () => (
    <div className="mb-12">
      <h2 className="text-2xl font-semibold mb-6" style={{ color: colors.text }}>
        Knowledge Levels by Awareness Segment
      </h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Some Awareness - HPV/CC Knowledge */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-1" style={{ color: colors.text }}>
            {knowledgeByAwareness.someAwareness.title}
          </h3>
          <p className="text-sm text-gray-500 mb-2">
            {knowledgeByAwareness.someAwareness.subtitle}
          </p>
          <p className="text-sm text-gray-600 mb-4">
            Population segment: {knowledgeByAwareness.someAwareness.totalPopulation}
          </p>
          
          <div className="mb-6 p-3 bg-teal-50 border border-teal-200 rounded-lg">
            <p className="text-sm text-teal-700">
              <strong>Strategic Opportunity:</strong> {knowledgeByAwareness.someAwareness.insight}
            </p>
          </div>
          
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={knowledgeByAwareness.someAwareness.data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={2}
                dataKey="percentage"
              >
                {knowledgeByAwareness.someAwareness.data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${value}%`, 'Population']} />
            </PieChart>
          </ResponsiveContainer>
          
          <div className="space-y-2 mt-4">
            {knowledgeByAwareness.someAwareness.data.map((item, index) => (
              <div key={index} className="flex items-center text-sm">
                <div className="w-4 h-4 rounded-full mr-3" style={{ backgroundColor: item.color }}></div>
                <span className="flex-grow">{item.level}</span>
                <span className="font-semibold">{item.percentage}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Low/No Awareness - Reproductive Health Knowledge */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-1" style={{ color: colors.text }}>
            {knowledgeByAwareness.lowNoAwareness.title}
          </h3>
          <p className="text-sm text-gray-500 mb-2">
            {knowledgeByAwareness.lowNoAwareness.subtitle}
          </p>
          <p className="text-sm text-gray-600 mb-4">
            Population segment: {knowledgeByAwareness.lowNoAwareness.totalPopulation}
          </p>
          
          <div className="mb-6 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm text-orange-700">
              <strong>Strategic Opportunity:</strong> {knowledgeByAwareness.lowNoAwareness.insight}
            </p>
          </div>
          
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={knowledgeByAwareness.lowNoAwareness.data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={2}
                dataKey="percentage"
              >
                {knowledgeByAwareness.lowNoAwareness.data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${value}%`, 'Population']} />
            </PieChart>
          </ResponsiveContainer>
          
          <div className="space-y-2 mt-4">
            {knowledgeByAwareness.lowNoAwareness.data.map((item, index) => (
              <div key={index} className="flex items-center text-sm">
                <div className="w-4 h-4 rounded-full mr-3" style={{ backgroundColor: item.color }}></div>
                <span className="flex-grow">{item.level}</span>
                <span className="font-semibold">{item.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <p className="text-sm text-blue-800">
          <strong>Key Observation:</strong> Both segments show similar knowledge distribution patterns, indicating standardized program approaches can be effective.
        </p>
      </div>
    </div>
  );

  const renderDemographicComparison = (comparison, index) => {
    return (
      <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
        <h3 className="text-xl font-semibold mb-2" style={{ color: colors.text }}>
          {comparison.title}
        </h3>
        <p className="text-gray-600 mb-6">{comparison.subtitle}</p>
        
        <div className="flex justify-center items-start gap-12 mb-6">
          {comparison.data.map((item, idx) => (
            <div key={idx} className="text-center">
              <h4 className="text-lg font-semibold mb-4" style={{ color: colors.text }}>
                {item.category}
              </h4>
              <div className="relative">
                <ResponsiveContainer width={200} height={200}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'High Knowledge', value: item.highKnowledge, color: item.color },
                        { name: 'Lower Knowledge', value: 100 - item.highKnowledge, color: '#E5E7EB' }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      startAngle={90}
                      endAngle={450}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      <Cell fill={item.color} />
                      <Cell fill="#E5E7EB" />
                    </Pie>
                    <Tooltip formatter={(value) => [`${value}%`, 'Percentage']} />
                  </PieChart>
                </ResponsiveContainer>
                
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div 
                      className="text-2xl font-bold"
                      style={{ color: item.color }}
                    >
                      {item.highKnowledge}%
                    </div>
                    <div className="text-xs text-gray-600">High Knowledge</div>
                  </div>
                </div>
              </div>
              
              <div className="mt-4">
                <div className="text-sm text-gray-600">Population: {item.population}</div>
                <div 
                  className="mt-2 px-3 py-1 rounded-full text-xs font-semibold"
                  style={{ 
                    backgroundColor: item.color, 
                    color: item.textColor 
                  }}
                >
                  {item.category}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            <strong>Strategic Opportunity:</strong> {comparison.insight}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-white">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-light mb-4" style={{ color: colors.text }}>
          Nigerians' HPV/CC Awareness & Knowledge
        </h1>
        <div className="w-full h-px bg-gray-300 mb-8"></div>
      </div>

      {/* Key Findings */}
      <div className="mb-12">
        <h2 className="text-2xl font-semibold text-center mb-8" style={{ color: colors.text }}>
          Key Findings
        </h2>
        
        <div className="flex justify-center items-start gap-16 mb-8">
          {keyFindings.map((finding, index) => (
            <div key={index} className="text-center flex-shrink-0">
              <div 
                className="text-6xl font-light mb-2" 
                style={{ color: finding.color }}
              >
                {finding.value}
              </div>
              <div className="text-lg font-medium mb-2" style={{ color: colors.text }}>
                {finding.label}
              </div>
              <div className="text-sm text-gray-600 max-w-48 mx-auto text-center">
                {finding.description}
              </div>
            </div>
          ))}
        </div>

        {/* Demographic Context */}
        <div className="bg-gray-50 p-6 rounded-lg">
          {demographicInfo.map((info, index) => (
            <div key={index} className="mb-3 last:mb-0">
              <span className="font-semibold text-gray-700">{info.label}</span>{' '}
              <span className="text-gray-600">{info.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Cervical Cancer Awareness Distribution */}
      <div className="mb-12">
        <h2 className="text-2xl font-semibold mb-6" style={{ color: colors.text }}>
          Cervical Cancer Awareness Distribution (Population-Adjusted)
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={awarenessLevels}
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="percentage"
                  label={({ level, percentage }) => `${percentage}%`}
                  labelLine={false}
                >
                  {awarenessLevels.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value}%`, 'Population']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="flex flex-col justify-center">
            <div className="space-y-4">
              {awarenessLevels.map((level, index) => (
                <div key={index} className="flex items-center">
                  <div 
                    className="w-6 h-6 rounded-full mr-4" 
                    style={{ backgroundColor: level.color }}
                  ></div>
                  <div className="flex-grow">
                    <div className="font-semibold text-lg">{level.level}: {level.percentage}%</div>
                    <div className="text-sm text-gray-600">{level.description}</div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                <div className="text-2xl font-bold mb-1" style={{ color: colors.onboarded }}>
                  51%
                </div>
                <div className="text-sm text-teal-700">Have Some Awareness</div>
                <div className="text-xs text-teal-600 mt-1">Ready for engagement programs</div>
              </div>
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="text-2xl font-bold mb-1" style={{ color: colors.journeyStarted }}>
                  49%
                </div>
                <div className="text-sm text-yellow-700">Low/No Awareness</div>
                <div className="text-xs text-yellow-600 mt-1">Primary education opportunity</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Knowledge Levels by Awareness Segment */}
      {renderKnowledgeByAwareness()}

      {/* Demographic Comparisons */}
      <div className="mb-12">
        <h2 className="text-2xl font-semibold mb-6" style={{ color: colors.text }}>
          Knowledge Patterns Across Demographics
        </h2>
        <p className="text-gray-600 mb-8 text-lg">
          Side-by-side comparisons reveal strategic opportunities for targeted health education programs
        </p>
        
        {demographicComparisons.map((comparison, index) => 
          renderDemographicComparison(comparison, index)
        )}
      </div>

      {/* Population Impact & Market Opportunity Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h2 className="text-2xl font-semibold mb-6" style={{ color: colors.text }}>
          Strategic Market Opportunities (Population-Adjusted)
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-6">
          <div className="text-center">
            <div className="text-3xl font-bold mb-2" style={{ color: colors.onboarded }}>
              ~407,000
            </div>
            <div className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
              Higher Awareness Population
            </div>
            <div className="text-sm text-gray-600">
              Ready for targeted HPV education and vaccination programs
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold mb-2" style={{ color: colors.journeyStarted }}>
              ~391,000
            </div>
            <div className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
              Lower Awareness Population
            </div>
            <div className="text-sm text-gray-600">
              Foundation health education and awareness building opportunity
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold mb-2" style={{ color: colors.redemption }}>
              798,000
            </div>
            <div className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
              Total Market Opportunity
            </div>
            <div className="text-sm text-gray-600">
              Lagos & Ogun State urban population, ages 15-45
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-3" style={{ color: colors.text }}>
            MRP Population-Adjusted Analysis Complete
          </h3>
          <p className="text-gray-700 mb-4">
            Comprehensive analysis reveals knowledge gaps are worse in the real population than initial survey data suggested, creating significant opportunities for targeted intervention.
          </p>
          <div className="text-sm text-gray-600">
            <strong>Status:</strong> ✅ MRP Bias Correction Applied | ✅ Demographic Patterns Identified | ✅ Integration Ready
          </div>
        </div>
      </div>
    </div>
  );
};

export default AwarenessKnowledgeTab;